import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, Edit, Play, ChevronRight, 
  QrCode, Crown, Trophy, Zap
} from 'lucide-react';
import { PollarBrandMini } from '@/components/PollarBrand';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const getWsUrl = () => {
  if (BACKEND_URL && BACKEND_URL.startsWith('http')) {
    return BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
  }
  const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
  return `${protocol}${window.location.host}`;
};

const SpeedRunFacilitator = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
  const [quizState, setQuizState] = useState('lobby'); // lobby, countdown, active, results, podium
  const [countdown, setCountdown] = useState(3);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [participantCount, setParticipantCount] = useState(0);
  const [topPerformers, setTopPerformers] = useState([]);
  const [finalLeaderboard, setFinalLeaderboard] = useState([]);
  const [showQRModal, setShowQRModal] = useState(false);
  const [revealStage, setRevealStage] = useState(0);
  const [ws, setWs] = useState(null);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const timerRef = useRef(null);
  const currentQuestionIndexRef = useRef(-1);

  useEffect(() => {
    fetchSession();
    connectWebSocket();
    
    return () => {
      if (ws) ws.close();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [sessionId]);


  // Podium reveal - show all at once
  useEffect(() => {
    if (quizState === 'podium') {
      setRevealStage(3); // Show all 3 immediately
    }
  }, [quizState]);


  const fetchSession = async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}`);
      const sessionData = response.data;
      setSession(sessionData);
      setParticipantCount(sessionData.participant_count || 0);
      
      console.log('DEBUG - Session status:', sessionData.status);
      console.log('DEBUG - Current question index:', sessionData.current_question_index);
      
      // Check if quiz is already active and resume state
      if (sessionData.status === 'active' && sessionData.current_question_index !== undefined && sessionData.current_question_index !== null) {
        const questionIndex = sessionData.current_question_index;
        const questions = sessionData.questions || [];
        const question = questions[questionIndex];
        
        if (question) {
          console.log('✓ Resuming quiz at question', questionIndex, 'with', question.time_limit, 's limit');
          setCurrentQuestionIndex(questionIndex);
          currentQuestionIndexRef.current = questionIndex; // Store in ref
          setQuizState('active');
          
          // Calculate remaining time based on server start time
          if (sessionData.current_question_start_time) {
            // Server stores the countdown start time
            // Question actually starts 3 seconds later
            const countdownStartTime = new Date(sessionData.current_question_start_time).getTime();
            const questionStartTime = countdownStartTime + 3000; // Add 3s for countdown
            
            const elapsed = (Date.now() - questionStartTime) / 1000;
            const remaining = Math.max(0, question.time_limit - elapsed);
            
            setQuestionStartTime(questionStartTime);
            setTimeRemaining(Math.ceil(remaining));
            
            console.log('DEBUG - Resume calculation: elapsed=', elapsed.toFixed(1), 'remaining=', remaining.toFixed(1));
            
            // Start timer
            if (timerRef.current) clearInterval(timerRef.current);
            
            timerRef.current = setInterval(() => {
              const elapsedNow = (Date.now() - questionStartTime) / 1000;
              const remainingNow = Math.max(0, question.time_limit - elapsedNow);
              setTimeRemaining(Math.ceil(remainingNow));
              
              if (remainingNow <= 0) {
                clearInterval(timerRef.current);
                endQuestion();
              }
            }, 100);
          }
        }
      } else if (sessionData.status === 'completed') {
        // Quiz already completed, show podium
        setQuizState('podium');
        fetchFinalLeaderboard();
      }
      
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load quiz');
      setLoading(false);
    }
  };

  const connectWebSocket = (isReconnect = false) => {
    const wsUrl = getWsUrl();
    const wsFullUrl = `${wsUrl}/api/ws/${sessionId}?role=facilitator`;
    console.log(isReconnect ? '🔄 Reconnecting WebSocket...' : 'Connecting WebSocket:', wsFullUrl);
    
    const websocket = new WebSocket(wsFullUrl);
    let pingInterval;
    let reconnectTimeout;
    
    websocket.onopen = () => {
      console.log('✅ WebSocket connected');
      
      // Start heartbeat ping every 30 seconds
      pingInterval = setInterval(() => {
        if (websocket.readyState === WebSocket.OPEN) {
          websocket.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
    };
    
    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      // Filter out heartbeat pong messages from logging
      if (data.type !== 'pong') {
        console.log('📨 WebSocket message received:', data);
      }
      
      if (data.type === 'participant_joined') {
        console.log('👤 Participant joined, updating count to:', data.participant_count);
        setParticipantCount(data.participant_count);
        toast.success(`${data.participant_name} joined! (${data.participant_count} total)`);
      } else if (data.type === 'participant_count') {
        console.log('📊 Participant count updated to:', data.count);
        setParticipantCount(data.count);
      }
      // Ignore pong messages (heartbeat)
    };
    
    websocket.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
    };
    
    websocket.onclose = () => {
      console.log('🔌 WebSocket disconnected');
      clearInterval(pingInterval);
      
      // Attempt reconnection after 2 seconds
      reconnectTimeout = setTimeout(() => {
        console.log('🔄 Attempting to reconnect...');
        connectWebSocket(true);
      }, 2000);
    };
    
    setWs(websocket);
    
    // Cleanup function
    return () => {
      clearInterval(pingInterval);
      clearTimeout(reconnectTimeout);
      if (websocket.readyState === WebSocket.OPEN) {
        websocket.close();
      }
    };
  };

  const handleStartQuiz = async () => {
    if (currentQuestionIndex === -1) {
      try {
        // Call backend to start quiz
        await axios.post(`${API}/speed-run/${sessionId}/start`);
        // Start first question
        showQuestion(0);
      } catch (error) {
        console.error('Failed to start quiz:', error);
        toast.error('Failed to start quiz');
      }
    }
  };

  const showQuestion = async (questionIndex) => {
    const questions = session?.questions || [];
    if (questionIndex >= questions.length) {
      // Quiz complete, show podium
      try {
        await axios.post(`${API}/speed-run/${sessionId}/end`);
      } catch (error) {
        console.error('Failed to end quiz:', error);
      }
      setQuizState('podium');
      fetchFinalLeaderboard();
      return;
    }

    const question = questions[questionIndex];
    setCurrentQuestionIndex(questionIndex);
    currentQuestionIndexRef.current = questionIndex; // Store in ref for timer access
    setQuizState('countdown');
    setCountdown(3);

    try {
      // Call backend to start question (backend handles countdown and broadcasts)
      await axios.post(`${API}/speed-run/${sessionId}/question/${questionIndex}/start`);
    } catch (error) {
      console.error('Failed to start question:', error);
    }

    // Local countdown for facilitator UI
    let count = 3;
    const countdownInterval = setInterval(() => {
      count--;
      setCountdown(count);
      if (count <= 0) {
        clearInterval(countdownInterval);
        startQuestion(question);
      }
    }, 1000);
  };

  const startQuestion = (question) => {
    setQuizState('active');
    
    // Set question start time to NOW (countdown already finished)
    const startTime = Date.now();
    setQuestionStartTime(startTime);
    
    // Calculate time remaining based on elapsed time
    const calculateTimeRemaining = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      const remaining = Math.max(0, question.time_limit - elapsed);
      return Math.ceil(remaining);
    };
    
    setTimeRemaining(calculateTimeRemaining());

    // Update timer every 100ms for smooth countdown
    timerRef.current = setInterval(() => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);
      
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        endQuestion();
      }
    }, 100);
  };

  const endQuestion = async () => {
    // Use ref to get the correct question index even if state hasn't updated
    const questionIndex = currentQuestionIndexRef.current;
    console.log('DEBUG - Ending question', questionIndex, '(state says:', currentQuestionIndex, ')');
    setQuizState('results');
    
    // Fetch top 3 performers for this question
    const questions = session?.questions || [];
    const question = questions[questionIndex];
    
    if (!question || !question.id) {
      console.error('Question not found for index:', questionIndex);
      setTopPerformers([]);
      return;
    }
    
    console.log('DEBUG - Fetching top performers for question ID:', question.id);
    
    try {
      // Call backend to end question and get top performers
      const response = await axios.post(`${API}/speed-run/${sessionId}/question/${question.id}/end`);
      console.log('DEBUG - Top performers received:', response.data.top_performers);
      setTopPerformers(response.data.top_performers || []);
    } catch (error) {
      console.error('Failed to end question:', error);
      // Fallback: try to fetch top performers directly
      try {
        const response = await axios.get(`${API}/speed-run/${sessionId}/question/${question.id}/top-performers`);
        console.log('DEBUG - Top performers (fallback):', response.data.top_performers);
        setTopPerformers(response.data.top_performers || []);
      } catch (err) {
        console.error('Failed to fetch top performers', err);
        setTopPerformers([]);
      }
    }
  };

  const handleNextQuestion = () => {
    showQuestion(currentQuestionIndex + 1);
  };

  const fetchFinalLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/speed-run/${sessionId}/leaderboard`);
      setFinalLeaderboard(response.data.participants || []);
    } catch (error) {
      console.error('Failed to fetch final leaderboard', error);
      setFinalLeaderboard([]);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Zap className="h-12 w-12 text-orange-500 mx-auto mb-4 animate-pulse" />
          <p className="text-lg text-gray-600">Loading quiz...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg text-gray-600">Quiz not found</p>
      </div>
    );
  }

  const questions = session.questions || [];
  const currentQuestion = currentQuestionIndex >= 0 ? questions[currentQuestionIndex] : null;

  // LOBBY STATE
  if (quizState === 'lobby') {
    return (
      <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
        {/* Top Bar */}
        <div className="bg-white shadow-sm border-b">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <PollarBrandMini />
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="ghost"
                  size="sm"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Dashboard
                </Button>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
                  <Users className="h-5 w-5 text-blue-600" />
                  <span className="font-bold text-blue-600">{participantCount}</span>
                </div>
                
              </div>
            </div>
          </div>
        </div>

        {/* Lobby Content */}
        <div className="container mx-auto px-4 py-12 max-w-4xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-orange-100 rounded-full">
              <Zap className="h-5 w-5 text-orange-600" />
              <span className="font-semibold text-orange-600">Speed Run Quiz</span>
            </div>
            <h1 className="text-5xl font-bold mb-4" style={{ color: '#1a1a1a' }}>
              {session.topic}
            </h1>
            <p className="text-xl text-gray-600">
              {questions.length} Questions • {session.is_anonymous ? 'Anonymous' : 'Named'} Participants
            </p>
          </div>

          {/* QR Code Card */}
          <Card className="mb-8 border-2 border-orange-500">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Scan to Join!</h2>
              <p className="text-gray-600 mb-6">
                Participants can scan this QR code to join the quiz
              </p>
              
              {session.qr_code_data && (
                <div className="flex justify-center mb-6">
                  <img 
                    src={session.qr_code_data} 
                    alt="QR Code" 
                    className="w-64 h-64 border-4 border-orange-500 rounded-lg"
                  />
                </div>
              )}
              
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">Or visit:</p>
                <div className="bg-gray-100 p-4 rounded-lg break-all">
                  <code className="text-sm font-mono">
                    {window.location.origin}/participant/{sessionId}
                  </code>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Start Button */}
          <Button
            onClick={handleStartQuiz}
            disabled={participantCount === 0}
            className="w-full py-8 text-2xl bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
          >
            <Play className="mr-3 h-8 w-8" />
            Start Speed Run Quiz
          </Button>
          
          {participantCount === 0 && (
            <p className="text-center text-gray-500 mt-4">
              Waiting for participants to join...
            </p>
          )}
        </div>
      </div>
    );
  }

  // COUNTDOWN STATE (3-2-1)
  if (quizState === 'countdown') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div 
            className="text-[20rem] font-bold text-white animate-pulse"
            style={{
              textShadow: '0 0 80px rgba(249, 115, 22, 0.8)',
              animation: 'pulse 0.5s ease-in-out'
            }}
          >
            {countdown}
          </div>
        </div>
      </div>
    );
  }

  // ACTIVE QUESTION STATE
  if (quizState === 'active' && currentQuestion) {
    return (
      <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #fff5f0 0%, #ffe8e0 100%)' }}>
        {/* Top Bar */}
        <div className="bg-white shadow-md border-b-4 border-orange-500">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <PollarBrandMini />
                
                {/* Mini QR Code with Hover */}
                <div 
                  className="relative cursor-pointer"
                  onMouseEnter={() => setShowQRModal(true)}
                  onMouseLeave={() => setShowQRModal(false)}
                >
                  <div className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                    <QrCode className="h-5 w-5 text-gray-600" />
                  </div>
                  
                  {showQRModal && session.qr_code_data && (
                    <div className="absolute top-12 left-0 z-50 bg-white p-4 rounded-lg shadow-2xl border-2 border-orange-500 min-w-[220px]">
                      <img 
                        src={session.qr_code_data} 
                        alt="QR Code" 
                        className="w-48 h-48 object-contain"
                        style={{ display: 'block' }}
                      />
                      <p className="text-xs text-center text-gray-600 mt-2">Scan to join</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
                  <Users className="h-5 w-5 text-blue-600" />
                  <span className="font-bold text-blue-600">{participantCount}</span>
                </div>
                
                <div className="flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-lg">
                  <span className="text-3xl font-bold text-orange-600">
                    {formatTime(timeRemaining)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Question Display */}
        <div className="container mx-auto px-4 py-12 max-w-5xl">
          <div className="text-center mb-8">
            <div className="inline-block px-4 py-2 bg-orange-500 text-white rounded-full font-bold mb-4">
              Question {currentQuestionIndex + 1} of {questions.length}
            </div>
          </div>

          <Card className="mb-8 border-4 border-orange-500">
            <CardContent className="p-12">
              {currentQuestion.image_url && (
                <div className="mb-8 flex justify-center">
                  <img 
                    src={currentQuestion.image_url} 
                    alt="Question" 
                    className="max-h-64 rounded-lg shadow-lg"
                  />
                </div>
              )}
              
              <h2 className="text-4xl font-bold text-center mb-8">
                {currentQuestion.text}
              </h2>

              {currentQuestion.response_type !== 'drag_reorder_ranking' && (
                <div className="grid grid-cols-2 gap-6">
                  {currentQuestion.options?.map((option, idx) => (
                    <div
                      key={option.id}
                      className={`p-6 rounded-xl border-4 ${
                        option.text === currentQuestion.correct_answer
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-300 bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-3xl font-bold text-orange-500">
                          {String.fromCharCode(65 + idx)}
                        </div>
                        {option.image_url ? (
                          <img src={option.image_url} alt={`Option ${idx + 1}`} className="h-20 w-20 object-cover rounded" />
                        ) : null}
                        <div className="flex-1 text-xl font-semibold">
                          {option.text}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {currentQuestion.response_type === 'drag_reorder_ranking' && (
                <div className="space-y-4">
                  {currentQuestion.options?.map((option, idx) => (
                    <div
                      key={option.id}
                      className="p-4 rounded-lg border-2 border-gray-300 bg-white flex items-center gap-4"
                    >
                      <div className="text-2xl font-bold text-orange-500">
                        {idx + 1}
                      </div>
                      <div className="flex-1 text-lg font-semibold">
                        {option.text}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="text-center">
            <div className="inline-block px-6 py-3 bg-blue-100 rounded-lg">
              <p className="text-sm text-gray-600">Points for correct answer</p>
              <p className="text-3xl font-bold text-blue-600">{currentQuestion.points}</p>
            </div>
            {currentQuestion.points_deducted > 0 && (
              <div className="inline-block px-6 py-3 bg-red-100 rounded-lg ml-4">
                <p className="text-sm text-gray-600">Points deducted if wrong</p>
                <p className="text-3xl font-bold text-red-600">-{currentQuestion.points_deducted}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // RESULTS STATE (Top 3 after question)
  if (quizState === 'results') {
    return (
      <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fcd34d 100%)' }}>
        {/* Top Bar */}
        <div className="bg-white shadow-md border-b-4 border-yellow-500">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <PollarBrandMini />
                
                {/* Mini QR Code with Hover */}
                <div 
                  className="relative cursor-pointer"
                  onMouseEnter={() => setShowQRModal(true)}
                  onMouseLeave={() => setShowQRModal(false)}
                >
                  <div className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                    <QrCode className="h-5 w-5 text-gray-600" />
                  </div>
                  
                  {showQRModal && session.qr_code_data && (
                    <div className="absolute top-12 left-0 z-50 bg-white p-4 rounded-lg shadow-2xl border-2 border-orange-500 min-w-[220px]">
                      <img 
                        src={session.qr_code_data} 
                        alt="QR Code" 
                        className="w-48 h-48 object-contain"
                        style={{ display: 'block' }}
                      />
                      <p className="text-xs text-center text-gray-600 mt-2">Scan to join</p>
                    </div>
                  )}
                </div>
              </div>
              
              <Button
                onClick={handleNextQuestion}
                className="bg-gradient-to-r from-orange-500 to-red-600"
              >
                <ChevronRight className="mr-2 h-5 w-5" />
                {currentQuestionIndex < questions.length - 1 ? 'Next Question' : 'View Final Results'}
              </Button>
            </div>
          </div>
        </div>

        {/* Top 3 Display */}
        <div className="container mx-auto px-4 py-12 max-w-4xl">
          <div className="text-center mb-12">
            <Trophy className="h-20 w-20 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-5xl font-bold mb-4">Fastest Correct Answers!</h1>
            <p className="text-xl text-gray-700">Question {currentQuestionIndex + 1}</p>
          </div>

          <div className="space-y-4">
            {topPerformers.map((performer, idx) => (
              <Card 
                key={idx}
                className={`border-4 transform transition-all duration-500 ${
                  idx === 0 
                    ? 'border-yellow-400 scale-105 shadow-2xl' 
                    : idx === 1 
                    ? 'border-gray-400 shadow-xl' 
                    : 'border-orange-400 shadow-lg'
                }`}
                style={{
                  animation: `slideIn 0.5s ease-out ${idx * 0.2}s both`
                }}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-5xl">
                        {idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}
                      </div>
                      <div>
                        <div className="text-2xl font-bold">{performer.participant_name}</div>
                        <div className="text-lg text-gray-600">
                          {performer.time_taken.toFixed(2)}s • +{performer.points_awarded} points
                        </div>
                      </div>
                    </div>
                    <div className="text-4xl font-bold text-orange-600">
                      #{performer.rank}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <style jsx>{`
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </div>
    );
  }

  // PODIUM STATE (Final results)
  if (quizState === 'podium') {
    return (
      <div className="min-h-screen relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)' }}>
        {/* Confetti effect */}
        {revealStage === 3 && (
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(50)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 bg-yellow-400 rounded-full animate-confetti"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${3 + Math.random() * 2}s`
                }}
              />
            ))}
          </div>
        )}

        <div className="container mx-auto px-4 py-12 max-w-6xl relative z-10">
          <div className="text-center mb-12">
            <Crown className="h-24 w-24 text-yellow-400 mx-auto mb-6 animate-bounce" />
            <h1 className="text-6xl font-bold text-white mb-4">
              Final Results
            </h1>
            <p className="text-2xl text-gray-300">{session.topic}</p>
          </div>

          {/* Podium */}
          <div className="flex items-end justify-center gap-8 mb-12">
            {/* 2nd Place */}
            {revealStage >= 2 && finalLeaderboard[1] && (
              <div 
                className="flex flex-col items-center animate-slideUp"
                style={{ animationDelay: '0.5s' }}
              >
                <div className="text-6xl mb-4">🥈</div>
                <div className="bg-gray-400 rounded-t-3xl p-8 text-center" style={{ height: '200px', width: '200px' }}>
                  <div className="text-3xl font-bold text-white mb-2">
                    {finalLeaderboard[1].participant_name.replace(/^Player\s+/i, '')}
                  </div>
                  <div className="text-5xl font-bold text-white">
                    {Number(finalLeaderboard[1].total_score).toFixed(2)}
                  </div>
                  <div className="text-sm text-gray-200 mt-2">points</div>
                </div>
                <div className="bg-gray-500 w-full text-center py-4 text-3xl font-bold text-white">
                  2
                </div>
              </div>
            )}

            {/* 1st Place (Tallest) */}
            {revealStage >= 3 && finalLeaderboard[0] && (
              <div 
                className="flex flex-col items-center animate-slideUp"
                style={{ animationDelay: '1s' }}
              >
                <div className="text-8xl mb-4 animate-bounce">🥇</div>
                <div className="bg-yellow-400 rounded-t-3xl p-8 text-center shadow-2xl" style={{ height: '280px', width: '240px' }}>
                  <div className="text-4xl font-bold text-gray-900 mb-4">
                    {finalLeaderboard[0].participant_name.replace(/^Player\s+/i, '')}
                  </div>
                  <div className="text-7xl font-bold text-gray-900">
                    {Number(finalLeaderboard[0].total_score).toFixed(2)}
                  </div>
                  <div className="text-lg text-gray-700 mt-2">points</div>
                </div>
                <div className="bg-yellow-500 w-full text-center py-6 text-4xl font-bold text-gray-900">
                  1
                </div>
              </div>
            )}

            {/* 3rd Place */}
            {revealStage >= 1 && finalLeaderboard[2] && (
              <div 
                className="flex flex-col items-center animate-slideUp"
              >
                <div className="text-6xl mb-4">🥉</div>
                <div className="bg-orange-400 rounded-t-3xl p-8 text-center" style={{ height: '160px', width: '200px' }}>
                  <div className="text-2xl font-bold text-white mb-2">
                    {finalLeaderboard[2].participant_name.replace(/^Player\s+/i, '')}
                  </div>
                  <div className="text-4xl font-bold text-white">
                    {Number(finalLeaderboard[2].total_score).toFixed(2)}
                  </div>
                  <div className="text-sm text-gray-200 mt-2">points</div>
                </div>
                <div className="bg-orange-500 w-full text-center py-4 text-3xl font-bold text-white">
                  3
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {revealStage === 3 && (
            <div className="text-center animate-fadeIn flex gap-4 justify-center">
              <Button
                onClick={() => navigate(`/speed-run/${sessionId}/summary`)}
                className="px-8 py-6 text-xl bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
              >
                <Trophy className="mr-2 h-6 w-6" />
                View Summary
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                className="px-8 py-6 text-xl bg-white text-gray-900 hover:bg-gray-100"
              >
                <ArrowLeft className="mr-2 h-6 w-6" />
                Back to Dashboard
              </Button>
            </div>
          )}
        </div>

        <style jsx>{`
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(100px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes confetti {
            0% {
              transform: translateY(-100vh) rotate(0deg);
              opacity: 1;
            }
            100% {
              transform: translateY(100vh) rotate(720deg);
              opacity: 0;
            }
          }
          
          .animate-slideUp {
            animation: slideUp 0.8s ease-out both;
          }
          
          .animate-fadeIn {
            animation: fadeIn 1s ease-out both;
          }
          
          .animate-confetti {
            animation: confetti linear infinite;
          }
        `}</style>
      </div>
    );
  }

  return null;
};

export default SpeedRunFacilitator;
